/**
 * Client-side helpers for the platform's optimistic-concurrency contract.
 *
 * The backend rejects a write built on stale data with HTTP 409 and a body:
 *   { success: false, error: { code: 'VERSION_CONFLICT', currentVersion } }
 * (see `aapastech-backend/src/lib/optimistic-concurrency.ts`).
 *
 * These helpers let any mutation's `onError` detect that case and reconcile —
 * instead of showing a generic "save failed" and letting the user retry into
 * the same clobber.
 */
import type { AxiosError } from 'axios';

/** Matches the backend `VERSION_CONFLICT_CODE`. */
export const VERSION_CONFLICT_CODE = 'VERSION_CONFLICT';

interface ConflictBody {
  error?: { code?: string; message?: string; currentVersion?: number };
}

/** True when an axios error is a 409 version conflict from our API. */
export function isVersionConflict(err: unknown): boolean {
  const ax = err as AxiosError<ConflictBody> | undefined;
  return ax?.response?.status === 409 && ax?.response?.data?.error?.code === VERSION_CONFLICT_CODE;
}

/** The server's current version, if the error is a version conflict. */
export function conflictCurrentVersion(err: unknown): number | undefined {
  const ax = err as AxiosError<ConflictBody> | undefined;
  return ax?.response?.data?.error?.currentVersion;
}

/** Best-effort human message from any API error, with a fallback. */
export function apiErrorMessage(err: unknown, fallback = 'Something went wrong'): string {
  const ax = err as AxiosError<{ error?: { message?: string } }> | undefined;
  return ax?.response?.data?.error?.message || (err as Error)?.message || fallback;
}
